import { ActionIcon, Button, Menu, TextInput, UnstyledButton } from '@mantine/core';
import { IconDots, IconPlus, IconRosetteDiscountCheck, IconSearch } from '@tabler/icons-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../EmptyState.js';
import { PageHeader } from '../../PageHeader.js';
import { Panel } from '../../Panel.js';
import { StatusBadge } from '../../StatusBadge.js';
import table from '../materials/Materials.module.css';
import { errorCode } from '../personnel/source.js';
import classes from './Purchasing.module.css';
import { AddSupplierDialog, NewSupplierBadge } from './RfqMarket.js';
import { sortItems } from './requisitions.js';
import { SupplierAccountDialog } from './SupplierAccountDialog.js';
import { SupplierDialog } from './SupplierDialog.js';
import { SupplierPortalDialog } from './SupplierPortalDialog.js';
import { SupplierProfileDialog } from './SupplierProfileDialog.js';
import type { SupplierSource } from './source.js';
import {
  LoadError,
  NoMatch,
  SortableTh,
  type SortState,
  TablePager,
  TableSkeleton,
} from './table.js';
import type { Supplier, SupplierList, SupplierPortalStatus } from './types.js';

const PAGE_SIZE = 10;

type Column = 'code' | 'name' | 'contact' | 'phone' | 'email' | 'portal' | 'status';

/** Portal durumunun rozet tonu (ADR 0112 §5). */
const PORTAL_TONE: Record<SupplierPortalStatus, 'success' | 'info' | 'neutral' | 'danger'> = {
  none: 'neutral',
  invited: 'info',
  active: 'success',
  revoked: 'danger',
  // Açık pazarda teklif veren, şirkete bağlı olmayan tedarikçi (ADR 0113 §4)
  prospect: 'info',
};
const PORTAL_ORDER: Record<SupplierPortalStatus, number> = {
  prospect: 0,
  active: 1,
  invited: 2,
  revoked: 3,
  none: 4,
};

const SORT_VALUE: Record<Column, (supplier: Supplier) => string | number | null> = {
  // Varsayılan: kod sırası (açılış sırası; tasarımdaki gibi), başlığı yok
  code: (supplier) => supplier.code,
  name: (supplier) => supplier.name,
  contact: (supplier) => supplier.contactPerson,
  phone: (supplier) => supplier.phone,
  email: (supplier) => supplier.email,
  portal: (supplier) => PORTAL_ORDER[supplier.portal?.status ?? 'none'],
  // Etkinler önce
  status: (supplier) => (supplier.active ? 0 : 1),
};

/** Bildirimin tonu: başarı yeşil `status`, hata kırmızı `alert`. */
type Notice = { tone: 'success' | 'danger'; text: string };

type State =
  | { kind: 'loading' }
  | { kind: 'error'; code: string }
  | { kind: 'ready'; list: SupplierList };

export interface SuppliersScreenProps {
  source: SupplierSource;
  /** Açılışta profili gösterilecek tedarikçi (zil bağlantısı `?profile=`; ADR 0114). */
  initialProfileId?: string | null;
  /**
   * Açılışta cari penceresi gösterilecek tedarikçi ve sekme (zil bağlantısı `?account=`;
   * ADR 0119 Ek 2026-10-24).
   */
  initialAccount?: { supplierId: string; tab: 'invoices' | 'payments' } | null;
  /** Profil ya da cari penceresi kapanınca (zil bağlantısının parametresi temizlensin). */
  onProfileClosed?: () => void;
  /** Profildeki inceleme sonrası (zil sayısı yenilensin). */
  onProfileChanged?: () => void;
}

/**
 * Tedarikçiler (`gemi-satin-alma.jpeg` "Supplier Management"; ADR 0026 §1): arama, "New
 * Supplier", Supplier Name / Contact Person / Phone / Email / Status / Actions tablosu ve
 * sayfalama. Yalnız ofiste; tedarikçi silinmez, pasifleştirilir.
 */
export function SuppliersScreen({
  source,
  initialProfileId,
  initialAccount,
  onProfileClosed,
  onProfileChanged,
}: SuppliersScreenProps) {
  const { t } = useTranslation();
  const k = (key: string, options: Record<string, unknown> = {}) =>
    t(`purchasing.suppliers.${key}`, options);
  const [state, setState] = useState<State>({ kind: 'loading' });
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState<Supplier | 'new' | null>(null);
  const [portalAction, setPortalAction] = useState<{
    mode: 'invite' | 'revoke';
    supplier: Supplier;
  } | null>(null);
  const [notice, setNotice] = useState<Notice | null>(null);
  // Açık pazardaki aday tedarikçinin şirket tedarikçisi yapılması (ADR 0113 §4)
  const [accepting, setAccepting] = useState<Supplier | null>(null);
  const [sort, setSort] = useState<SortState<Column>>({ key: 'code', dir: 'asc' });
  // Şirket profili ve banka hesapları (ADR 0114)
  const [profile, setProfile] = useState<Supplier | null>(null);
  // Cari, ekstre ve geçmiş (ADR 0119)
  const [account, setAccount] = useState<Supplier | null>(null);
  const hasProfile = (supplier: Supplier) =>
    source.profile !== undefined &&
    (supplier.portal?.status === 'active' || supplier.portal?.status === 'prospect');

  const load = useCallback(async () => {
    try {
      setState({ kind: 'ready', list: await source.list() });
    } catch (error) {
      setState({ kind: 'error', code: errorCode(error) });
    }
  }, [source]);
  useEffect(() => {
    void load();
  }, [load]);

  const ready = state.kind === 'ready' ? state.list : null;
  // Zil bağlantısı: liste gelince o tedarikçinin profili açılır
  useEffect(() => {
    if (!initialProfileId || !ready) return;
    const found = ready.items.find((item) => item.id === initialProfileId);
    if (found) setProfile(found);
  }, [initialProfileId, ready]);
  // Zil bağlantısı: cari penceresi ilgili sekmede
  const accountTarget = initialAccount?.supplierId ?? null;
  useEffect(() => {
    if (!accountTarget || !ready) return;
    const found = ready.items.find((item) => item.id === accountTarget);
    if (found) setAccount(found);
  }, [accountTarget, ready]);
  const items = useMemo(() => {
    const q = query.trim().toLocaleLowerCase();
    const found = (ready?.items ?? []).filter(
      (s) =>
        !q ||
        [s.name, s.code, s.contactPerson, s.email, s.phone].some((value) =>
          (value ?? '').toLocaleLowerCase().includes(q),
        ),
    );
    return sortItems(found, sort.dir, SORT_VALUE[sort.key]);
  }, [ready, query, sort]);
  const pages = Math.max(1, Math.ceil(items.length / PAGE_SIZE));
  const current = Math.min(page, pages);
  const visible = items.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);

  const toggle = async (supplier: Supplier) => {
    try {
      const { id: _id, code: _code, rowVersion, ...input } = supplier;
      const saved = await source.update(supplier.id, rowVersion, {
        ...input,
        active: !supplier.active,
      });
      setNotice({ tone: 'success', text: k('saved', { name: saved.name }) });
      await load();
    } catch (error) {
      const code = errorCode(error);
      setNotice({
        tone: 'danger',
        text: t(`purchasing.errors.${code}`, { defaultValue: t('purchasing.errors.GENERIC') }),
      });
      // Başkası değiştirdiyse liste yenilenir: sonraki deneme güncel sürümle
      if (code === 'ROW_VERSION_CONFLICT') await load();
    }
  };

  return (
    <div className={classes.screen}>
      <PageHeader title={k('title')} description={k('description')} />
      {notice ? (
        <p
          className={classes.notice}
          data-tone={notice.tone}
          role={notice.tone === 'danger' ? 'alert' : 'status'}
        >
          {notice.text}
        </p>
      ) : null}

      <Panel flush aria-label={k('table')}>
        <div className={classes.searchRow}>
          <TextInput
            leftSection={<IconSearch size={16} aria-hidden />}
            placeholder={k('search')}
            aria-label={k('search')}
            value={query}
            onChange={(event) => {
              setQuery(event.currentTarget.value);
              setPage(1);
            }}
          />
          {ready?.canEdit ? (
            <Button leftSection={<IconPlus size={16} />} onClick={() => setEditing('new')}>
              {k('new')}
            </Button>
          ) : null}
        </div>

        {state.kind === 'error' ? (
          <LoadError title={k('error')} code={state.code} onRetry={() => void load()} />
        ) : state.kind === 'loading' ? (
          <TableSkeleton columns={source.portal ? 7 : 6} label={k('table')} />
        ) : (ready?.items.length ?? 0) === 0 ? (
          <EmptyState title={k('emptyTitle')} description={k('emptyText')} />
        ) : items.length === 0 ? (
          <NoMatch
            title={k('noMatch')}
            onClear={() => {
              setQuery('');
              setPage(1);
            }}
          />
        ) : (
          <>
            <section
              className={table.tableWrap}
              aria-label={k('table')}
              // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge (WCAG 2.1.1)
              tabIndex={0}
            >
              <table className={`${table.table} ${classes.table}`}>
                <thead>
                  <tr>
                    {(
                      [
                        'name',
                        'contact',
                        'phone',
                        'email',
                        ...(source.portal ? (['portal'] as const) : []),
                        'status',
                      ] as const
                    ).map((column) => (
                      <SortableTh
                        key={column}
                        column={column}
                        label={k(`columns.${column}`)}
                        sort={sort}
                        onSort={setSort}
                        {...(column === 'name' ? { className: classes.key } : {})}
                      />
                    ))}
                    <th scope="col" data-numeric>
                      {k('columns.actions')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map((supplier) => (
                    <tr key={supplier.id}>
                      <td className={`${table.itemName} ${classes.key}`}>
                        {hasProfile(supplier) ? (
                          <UnstyledButton
                            className={classes.linkName}
                            aria-label={t('supplierProfile.openOf', { name: supplier.name })}
                            onClick={() => setProfile(supplier)}
                          >
                            {supplier.name}
                          </UnstyledButton>
                        ) : (
                          supplier.name
                        )}
                        {supplier.newSupplier ? (
                          <span className={classes.flags}>
                            <NewSupplierBadge />
                          </span>
                        ) : null}
                      </td>
                      <td>{supplier.contactPerson ?? '—'}</td>
                      <td>{supplier.phone ?? '—'}</td>
                      <td>{supplier.email ?? '—'}</td>
                      {source.portal ? (
                        <td>
                          <div className={classes.portalCell}>
                            <span className={classes.portalBadges}>
                              <StatusBadge tone={PORTAL_TONE[supplier.portal?.status ?? 'none']}>
                                {k(`portal.status.${supplier.portal?.status ?? 'none'}`)}
                              </StatusBadge>
                              {supplier.portal?.verified ? (
                                <StatusBadge
                                  tone="info"
                                  icon={<IconRosetteDiscountCheck size={12} aria-hidden />}
                                >
                                  {k('portal.verified')}
                                </StatusBadge>
                              ) : null}
                              {supplier.portal?.suspended ? (
                                <StatusBadge tone="danger">{k('portal.suspended')}</StatusBadge>
                              ) : null}
                            </span>
                            {(supplier.portal?.status === 'active' ||
                              supplier.portal?.status === 'prospect') &&
                            supplier.portal.accountName ? (
                              <span className={classes.portalMeta}>
                                {supplier.portal.accountName}
                              </span>
                            ) : supplier.portal?.status === 'invited' && supplier.portal.email ? (
                              <span className={classes.portalMeta}>{supplier.portal.email}</span>
                            ) : null}
                          </div>
                        </td>
                      ) : null}
                      <td>
                        <StatusBadge tone={supplier.active ? 'success' : 'neutral'}>
                          {k(supplier.active ? 'active' : 'inactive')}
                        </StatusBadge>
                      </td>
                      <td data-numeric>
                        {ready?.canEdit || source.account ? (
                          <Menu position="bottom-end" withinPortal>
                            <Menu.Target>
                              <ActionIcon
                                variant="subtle"
                                color="gray"
                                aria-label={k('actionsOf', { name: supplier.name })}
                              >
                                <IconDots size={16} />
                              </ActionIcon>
                            </Menu.Target>
                            <Menu.Dropdown>
                              {ready?.canEdit ? (
                                <Menu.Item onClick={() => setEditing(supplier)}>
                                  {k('edit')}
                                </Menu.Item>
                              ) : null}
                              {hasProfile(supplier) ? (
                                <Menu.Item onClick={() => setProfile(supplier)}>
                                  {t('supplierProfile.open')}
                                </Menu.Item>
                              ) : null}
                              {source.account ? (
                                <Menu.Item onClick={() => setAccount(supplier)}>
                                  {t('supplierAccount.open')}
                                </Menu.Item>
                              ) : null}
                              {ready?.canEdit ? (
                                <Menu.Item onClick={() => void toggle(supplier)}>
                                  {k(supplier.active ? 'deactivate' : 'activate')}
                                </Menu.Item>
                              ) : null}
                              {source.portal && ready?.canEdit ? (
                                <>
                                  <Menu.Divider />
                                  {supplier.portal?.status === 'prospect' &&
                                  source.portal.accept ? (
                                    <Menu.Item onClick={() => setAccepting(supplier)}>
                                      {t('purchasingMarket.addSupplier')}
                                    </Menu.Item>
                                  ) : null}
                                  {supplier.portal?.status === 'active' ||
                                  supplier.portal?.status === 'prospect' ? null : (
                                    <Menu.Item
                                      disabled={!supplier.active || !supplier.approved}
                                      onClick={() => setPortalAction({ mode: 'invite', supplier })}
                                    >
                                      {k(
                                        supplier.portal?.status === 'invited'
                                          ? 'portal.resendAction'
                                          : 'portal.inviteAction',
                                      )}
                                    </Menu.Item>
                                  )}
                                  {supplier.portal?.status === 'active' ||
                                  supplier.portal?.status === 'invited' ||
                                  supplier.portal?.status === 'prospect' ? (
                                    <Menu.Item
                                      color="red"
                                      onClick={() => setPortalAction({ mode: 'revoke', supplier })}
                                    >
                                      {k('portal.revokeAction')}
                                    </Menu.Item>
                                  ) : null}
                                </>
                              ) : null}
                            </Menu.Dropdown>
                          </Menu>
                        ) : (
                          '—'
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
            <TablePager
              page={current}
              pages={pages}
              summary={k('showing', {
                from: (current - 1) * PAGE_SIZE + 1,
                to: Math.min(current * PAGE_SIZE, items.length),
                total: items.length,
              })}
              label={k('pagination')}
              onPage={setPage}
            />
          </>
        )}
      </Panel>

      {accepting && source.portal?.accept ? (
        <AddSupplierDialog
          name={accepting.name}
          onClose={() => setAccepting(null)}
          onConfirm={async () => {
            await source.portal?.accept?.(accepting.id);
            setAccepting(null);
            setNotice({
              tone: 'success',
              text: t('purchasingMarket.added', { name: accepting.name }),
            });
            await load();
          }}
        />
      ) : null}
      {portalAction && source.portal ? (
        <SupplierPortalDialog
          key={`${portalAction.mode}-${portalAction.supplier.id}`}
          mode={portalAction.mode}
          supplier={portalAction.supplier}
          portal={source.portal}
          onClose={() => setPortalAction(null)}
          onDone={(message) => {
            setPortalAction(null);
            setNotice({ tone: 'success', text: message });
            void load();
          }}
        />
      ) : null}

      {profile && source.profile ? (
        <SupplierProfileDialog
          key={profile.id}
          supplierId={profile.id}
          supplierName={profile.name}
          source={source.profile}
          onClose={() => {
            setProfile(null);
            onProfileClosed?.();
          }}
          onChanged={(message) => {
            setNotice({ tone: 'success', text: message });
            onProfileChanged?.();
          }}
        />
      ) : null}
      {account && source.account ? (
        <SupplierAccountDialog
          key={account.id}
          supplierId={account.id}
          supplierName={account.name}
          source={source.account}
          {...(initialAccount?.supplierId === account.id ? { initialTab: initialAccount.tab } : {})}
          onClose={() => {
            setAccount(null);
            if (initialAccount) onProfileClosed?.();
          }}
          onChanged={(message) => {
            setNotice({ tone: 'success', text: message });
            // Karar zil sayısını değiştirir
            onProfileChanged?.();
          }}
        />
      ) : null}
      {editing ? (
        <SupplierDialog
          key={editing === 'new' ? 'new' : editing.id}
          opened
          supplier={editing === 'new' ? null : editing}
          source={source}
          onClose={() => setEditing(null)}
          onSaved={(saved) => {
            setEditing(null);
            setNotice({ tone: 'success', text: k('saved', { name: saved.name }) });
            void load();
          }}
        />
      ) : null}
    </div>
  );
}
